import {
  $generateNodesFromSerializedNodes,
  $insertGeneratedNodes,
  type BaseSerializedNode,
} from "@lexical/clipboard";
import { $isCodeNode } from "@lexical/code-core";
import { $findMatchingParent } from "@lexical/utils";
import {
  $exportNodeJSON,
  $getEditor,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isDecoratorNode,
  $isElementNode,
  $isNodeSelection,
  $isRangeSelection,
  type BaseSelection,
  type LexicalNode,
  type NodeKey,
} from "lexical";
import { readBlockId } from "../../core/block-id";
import type { FragmentResult } from "../../core/clipboard";
import { tableLimits, type DocumentLimits } from "../../core/limits";
import type { BlockId, SerializedNodeLike } from "../../core/types";
import { $assignFreshBlockId, $continueBlockId } from "../block-id/extension";
import { $getBlockId, $isIdentified } from "../block-id/state";
import { $repairListItemsAbove } from "../lists/repair";

/** Which form of the content was read. */
export type FragmentSource = "internal" | "html" | "text" | "markdown";

/** Nodes that are not in the document yet, as the JSON a stored document would hold. */
export function $serializeNodes(nodes: readonly LexicalNode[]): SerializedNodeLike[] {
  return nodes.map((node) => {
    const serialized = $exportNodeJSON(node) as SerializedNodeLike;
    if ($isElementNode(node)) serialized.children = $serializeNodes(node.getChildren());
    return serialized;
  });
}

/**
 * How deep the block holding the selection sits: 1 at the root. Pasted blocks usually become its
 * siblings, one level up from this, but a list item can take a pasted list as its own child, so
 * the check below assumes the deeper of the two.
 */
function $levelsAbove(selection: BaseSelection): number {
  const first = $isRangeSelection(selection)
    ? selection.anchor.getNode()
    : $isNodeSelection(selection)
      ? (selection.getNodes()[0] ?? null)
      : null;
  let block: LexicalNode | null = first;
  while (block !== null && !($isElementNode(block) && !block.isInline())) block = block.getParent();
  let levels = 0;
  for (let parent = block?.getParent() ?? null; parent !== null; parent = parent.getParent()) {
    levels++;
  }
  return levels;
}

/** The block a selection's anchor is in: a list item rather than its list. */
function $identifiedBlockOf(node: LexicalNode | null): LexicalNode | null {
  for (let at = node; at !== null; at = at.getParent()) {
    if ($isIdentified(at) && at.getType() !== "list") return at;
  }
  return null;
}

interface PasteTarget {
  key: string;
  id: BlockId;
  headKey: string;
}

/**
 * Blocks pasted at the very start of a block do not go into it. Lexical splits it there, puts the
 * pasted blocks in, and either moves what the block held into the last of them and removes the
 * block, or leaves the block holding pasted content with its own content in a new one after it.
 * Wherever that content is, it is still the same block to the reader, so it has to keep its ID
 * (amendment 2). The identity transform cannot see it: by the time it runs, text of one format has
 * been merged into the pasted text in front of it, and the key it would follow is gone. So the
 * block is remembered before the paste, and `$continueBlock` names its successor right after.
 */
function $blockAtStart(
  selection: BaseSelection,
  pasted: readonly SerializedNodeLike[],
): PasteTarget | null {
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) return null;
  const block = $identifiedBlockOf(selection.anchor.getNode());
  const id = block === null ? null : $getBlockId(block);
  const head = $isElementNode(block) ? block.getFirstChild() : null;
  if (block === null || id === null || head === null) return null;
  // A copy of the block itself pasted at its own start brings the same ID; leave that to the
  // transform, which settles who keeps it.
  const stack = [...pasted];
  for (let node = stack.pop(); node !== undefined; node = stack.pop()) {
    if (readBlockId(node) === id) return null;
    if (node.children !== undefined) stack.push(...node.children);
  }
  return { key: block.getKey(), id, headKey: head.getKey() };
}

function $continueBlock(target: PasteTarget): void {
  const original = $getNodeByKey(target.key);
  const attached = original !== null && original.isAttached();
  // Wherever its first child went is the block that continues it. If that child is gone as well,
  // it was merged into the end of the pasted text, which is where the caret is.
  const head = $getNodeByKey(target.headKey);
  const selection = $getSelection();
  const successor =
    head !== null && head.isAttached()
      ? $identifiedBlockOf(head)
      : !attached && $isRangeSelection(selection)
        ? $identifiedBlockOf(selection.anchor.getNode())
        : null;
  if (successor === null || successor.getKey() === target.key) return;
  // The block is still there, but holding what was pasted while its own content went on into a
  // new one (a list item takes the first pasted block's children). The content keeps the ID.
  if (attached) $assignFreshBlockId(original);
  $continueBlockId(successor, target.id);
}

const $isInCode = (node: LexicalNode): boolean => $findMatchingParent(node, $isCodeNode) !== null;

/** By type: the table nodes load on demand, and this file is part of every editor. */
const $cellOf = (node: LexicalNode): LexicalNode | null =>
  $findMatchingParent(node, (parent) => parent.getType() === "tablecell");

/**
 * Whether pasting would grow a table past its limits. A table pasted with the selection in a cell
 * is laid over the cells from there, as in a spreadsheet, and upstream adds the rows and columns
 * it needs. Inserting rows and columns one at a time stops at the limits, and so must this, or the
 * editor saves a table it will refuse to open. The size of the result is known before anything is
 * inserted: there are no merged cells, so a cell's place in its row is its column.
 */
function $wouldOutgrowTable(
  selection: BaseSelection,
  pasted: readonly SerializedNodeLike[],
  limits: DocumentLimits,
): boolean {
  const only = pasted.length === 1 ? pasted[0] : undefined;
  if (only === undefined || only.type !== "table") return false;
  const points = $isRangeSelection(selection)
    ? [selection.anchor.getNode(), selection.focus.getNode()]
    : selection.getNodes().slice(0, 1);
  const cells = points.map($cellOf).filter((cell) => cell !== null);
  const first = cells[0];
  const table = first?.getParent()?.getParent() ?? null;
  if (first === undefined || table === null || !$isElementNode(table)) return false;
  const firstRow = table.getFirstChild();
  const rows = table.getChildrenSize();
  const columns = $isElementNode(firstRow) ? firstRow.getChildrenSize() : 0;
  // Where the pasted table's first cell lands: the top-left of what is selected.
  const row = Math.min(...cells.map((cell) => cell.getParent()?.getIndexWithinParent() ?? 0));
  const column = Math.min(...cells.map((cell) => cell.getIndexWithinParent()));
  const pastedRows = only.children ?? [];
  const pastedColumns = Math.max(0, ...pastedRows.map((line) => (line.children ?? []).length));
  const max = tableLimits(limits);
  return (
    Math.max(rows, row + pastedRows.length) > max.rows ||
    Math.max(columns, column + pastedColumns) > max.columns
  );
}

/**
 * Inserts checked content at the selection. Everything that can refuse does so before the
 * document is touched: nesting that would take the document past its own depth limit, and JSON
 * the engine will not parse although the guards accepted it.
 */
export function $insertFragment(
  fragment: Extract<FragmentResult, { status: "ok" }>,
  selection: BaseSelection,
  limits: DocumentLimits,
): "inserted" | "too-deep" | "table-too-large" | "unparseable" {
  if (fragment.nodes.length === 0) return "unparseable";
  if ($levelsAbove(selection) + fragment.depth > limits.maxDepth) return "too-deep";
  if ($wouldOutgrowTable(selection, fragment.nodes, limits)) return "table-too-large";
  let nodes: LexicalNode[];
  try {
    nodes = $generateNodesFromSerializedNodes(fragment.nodes as BaseSerializedNode[]);
  } catch {
    return "unparseable";
  }
  if ($isRangeSelection(selection) && $isInCode(selection.anchor.getNode())) {
    // Code is literal text. Upstream's insert into a code block keeps the first pasted node when
    // it is code and drops the rest, and otherwise puts whole blocks inside the code node; the
    // text of everything, a line per block, is what a code block can actually hold.
    selection.insertRawText(nodes.map((node) => node.getTextContent()).join("\n"));
    return "inserted";
  }
  const target = $blockAtStart(selection, fragment.nodes);
  $insertGeneratedNodes($getEditor(), nodes, selection);
  if (target !== null) $continueBlock(target);
  for (const node of nodes) if (node.isAttached()) $repairListItemsAbove(node);
  return "inserted";
}

/** Where blocks go when they are put somewhere by position: beside a top-level block. */
export type BlockPlace = { after: NodeKey } | { before: NodeKey };

/**
 * Puts checked blocks at the top level, beside the block `place` names, or at the end of the
 * document when it names none or the block is gone. Unlike `$insertFragment` it takes no selection
 * and leaves the one there is alone: this is for content that arrives on its own time (a file that
 * has finished staging), when whoever is writing is somewhere else by now. Answers with the nodes
 * it inserted, or `null`, having changed nothing, when the content cannot stand at the top level.
 */
export function $insertBlocks(
  fragment: Extract<FragmentResult, { status: "ok" }>,
  place: BlockPlace | null,
  limits: DocumentLimits,
): LexicalNode[] | null {
  // The root is one level, and the blocks stand directly under it.
  if (fragment.nodes.length === 0 || 1 + fragment.depth > limits.maxDepth) return null;
  let nodes: LexicalNode[];
  try {
    nodes = $generateNodesFromSerializedNodes(fragment.nodes as BaseSerializedNode[]);
  } catch {
    return null;
  }
  const isBlock = (node: LexicalNode): boolean =>
    ($isElementNode(node) || $isDecoratorNode(node)) && !node.isInline();
  if (!nodes.every(isBlock)) return null;
  const beside =
    place === null ? null : $getNodeByKey("after" in place ? place.after : place.before);
  const root = $getRoot();
  if (beside === null || !root.is(beside.getParent())) root.append(...nodes);
  else if (place !== null && "before" in place) for (const node of nodes) beside.insertBefore(node);
  else nodes.toReversed().forEach((node) => beside.insertAfter(node));
  return nodes;
}
